import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { ArrowDown, ArrowUp, Film, Upload, Rocket, Clock } from 'lucide-react';
import { useT } from '../i18n/useT';

/* ONE BUBBLE ENGINE, TWO TOURS.
 *
 * The composer tour below and the generating-card hint on the Workspace are the same object —
 * a card with a tail pointing at something, a spotlight around it, and a button that puts it
 * away — and the placement maths underneath is the hard part of both. A second copy of it is
 * how the two drift into disagreeing about where a bubble goes on a phone, which is the exact
 * bug this file has already been fixed for twice. So the engine takes a list of steps and a
 * storage key, and each tour is a few lines of data.
 *
 * The copy lives in i18n/en.js under the step's `textKey` — only en.js is edited by hand, the
 * other languages are generated from it (scripts/sync_i18n.mjs) and useT falls back to English
 * for a key that has not been translated yet. */

const STORAGE_KEY = 'magi_onboarding_done';
// Separate key, deliberately: someone who has used the composer for a month has not
// necessarily been told what a generating tile is, and vice versa.
const GENERATING_KEY = 'magi_onboarding_generating_done';

const COMPOSER_STEPS = [
  { target: '[data-onboarding="textarea"]', textKey: 'onboarding_step1', pointer: 'below', Icon: Film },
  { target: '[data-onboarding="upload"]',   textKey: 'onboarding_step2', pointer: 'above', Icon: Upload },
  { target: '[data-onboarding="submit"]',   textKey: 'onboarding_step3', pointer: 'above', Icon: Rocket },
];

/* The tile is near the top of the Workspace and a phone has no room above it, so `below` is
   both the preference and what the flip would choose anyway. */
const GENERATING_STEPS = [
  { target: '[data-onboarding="generating"]', textKey: 'onboarding_generating', pointer: 'below', Icon: Clock },
];

const BubbleTour = ({ steps, storageKey, active = true, seenOnShow = false }) => {
  const t = useT();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  /* THE BUBBLE'S OWN HEIGHT, because placement cannot be decided without it.
     Steps 2 and 3 point UP from controls that sit near the top of a phone screen, and the
     anchor was `bottom: innerHeight - rect.top + tail` with nothing bounding it — so the
     taller the text wrapped, the further off the TOP of the screen the bubble went. On a
     desktop there is room above those controls and the bug never shows. */
  const bubbleRef = useRef(null);
  const [bubbleH, setBubbleH] = useState(0);
  /* AND ITS WIDTH, for the same reason on the other axis. The horizontal clamp used the 384px
     CAP rather than the measured box, so a bubble whose text wrapped short was pushed further
     from the edge than it needed to be — and one at full width was clamped against a boundary
     that did not match what was drawn. */
  const [bubbleW, setBubbleW] = useState(0);

  /* The flag is re-read on every activation, which is what keeps `seenOnShow` below honest:
     a tour that goes inactive puts itself away rather than staying `visible` in state, so the
     next activation has to pass the storage check again — and by then it has been written.
     Without the reset the generating hint would reappear on the second generation, having
     recorded itself as seen on the first. The composer tour never goes inactive. */
  useEffect(() => {
    if (!active) { setVisible(false); return; }
    if (localStorage.getItem(storageKey) !== 'true') {
      setVisible(true);
    }
  }, [active, storageKey]);

  const reposition = useCallback(() => {
    if (!visible || !active) return;
    const el = document.querySelector(steps[step].target);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    /* THE RAIL IS PART OF THE BOUNDARY, not decoration. It is position:fixed over the left of
       the viewport, so a bubble clamped to the VIEWPORT edge slides underneath it and loses
       its first ~40px — the progress dots first, then the text. Navigation publishes its own
       width here (56px collapsed, 200px expanded, 0 below 768px), which is the same variable
       <main> and the legal footer already pad by. Read at reposition time so a step that opens
       while the rail is expanded clamps against the width actually on screen. */
    const rail = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--magi-sidebar')
    ) || 0;
    /* AND SO IS THE HEADER, on a phone. Below 768px that same nav is not a rail at all — it is
       a full-width bar pinned to top-0 with h-16 — so the top 64px of the viewport is covered
       and a bubble clamped to y=16 lands behind it. At md and up the nav IS the rail, whose
       height is the whole viewport, and reading that as a top inset would push every bubble off
       the bottom of the page. The rail's width already tells the two apart, because Navigation
       publishes 0px for exactly the widths where the nav is a top bar. */
    const nav = document.querySelector('nav[aria-label="Main navigation"]');
    const topBar = rail > 0 ? 0 : (nav ? nav.getBoundingClientRect().height : 0);
    setPos({ rect, pointer: steps[step].pointer, rail, topBar });
  }, [step, visible, active, steps]);

  useEffect(() => {
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    /* Expanding the rail from 56px to 200px moves the composer — and therefore the target —
       without firing resize or scroll, so neither listener above sees it and the bubble is
       left pointing at where the control used to be. */
    const rail = document.querySelector('nav[aria-label="Main navigation"]');
    const ro = rail ? new ResizeObserver(reposition) : null;
    if (ro && rail) ro.observe(rail);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      if (ro) ro.disconnect();
    };
  }, [reposition]);

  /* THE TARGET MAY NOT BE IN THE DOM ON THE FRAME THIS MOUNTS. The composer tour is safe —
     it opens on a page that is already painted — but the generating hint activates in the same
     render that adds the tile it points at, and a Workspace row that is still gated renders
     nothing at all until its observer fires. reposition() silently does nothing when the
     selector misses, and with no listener to retry it the bubble would simply never appear.
     Bounded at ~1s of frames, and inert the moment a position is found. */
  useEffect(() => {
    if (!visible || !active || pos) return;
    let frames = 0;
    let id = requestAnimationFrame(function retry() {
      reposition();
      if (++frames < 60) id = requestAnimationFrame(retry);
    });
    return () => cancelAnimationFrame(id);
  }, [visible, active, pos, reposition]);

  // Measured before paint, so the flip below decides on the real height rather than on a
  // guess, and the bubble never shows for a frame in the place it is about to leave.
  useLayoutEffect(() => {
    if (!bubbleRef.current) return;
    setBubbleH(bubbleRef.current.offsetHeight);
    setBubbleW(bubbleRef.current.offsetWidth);
  }, [step, pos, visible, transitioning]);

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setVisible(false);
  };

  /* SHOWN IS SPENT, for a tour that only exists while something else is on screen. The
     generating hint lives and dies with the tile it points at: the tile is replaced by the
     real video a minute later, so a hint that is only remembered when it is DISMISSED would
     come back on every generation for anyone who ignored it once. The composer tour has
     nothing to race — it sits on Home until it is answered — so it keeps the old behaviour. */
  useEffect(() => {
    if (seenOnShow && visible && pos) localStorage.setItem(storageKey, 'true');
  }, [seenOnShow, visible, pos, storageKey]);

  const next = () => {
    if (step < steps.length - 1) {
      setTransitioning(true);
      setTimeout(() => {
        setStep(step + 1);
        setTransitioning(false);
      }, 150);
    } else {
      dismiss();
    }
  };

  if (!visible || !active || !pos) return null;

  const { Icon } = steps[step];

  // Gap between target and first tail circle
  const TAIL_GAP = 14;
  // Tail circle sizes
  const CIRCLE_1 = 10; // closest to target
  const CIRCLE_2 = 16;
  // Gap between circles and bubble
  const BUBBLE_GAP = 10;
  const TOTAL_TAIL = TAIL_GAP + CIRCLE_1 + 8 + CIRCLE_2 + BUBBLE_GAP;

  /* Bubble centre X = target centre, clamped to the CONTENT box — the viewport minus the
     fixed rail — not to the viewport.

     `left` is a centre because the bubble is translateX(-50%), so the floor has to carry half
     a bubble as well as the padding. With the old floor of EDGE_PAD + width/2 the left edge
     landed 16px from the viewport edge, which is 40px underneath a 56px rail; on a wide screen
     that clipped the progress dots and the start of the card. Adding the rail moves the floor
     by exactly the rail's width and nothing else.

     BY ASPECT RATIO, WITHOUT BRANCHING ON ONE. Under 768px Navigation publishes 0px, so
     `rail` is 0, the floor is the old expression and phone behaviour is unchanged — where
     BUBBLE_MAX_W is innerWidth - 32 the floor and ceiling meet and the bubble is centred,
     exactly as before. There is no breakpoint here because the variable already is one.

     Ceiling wins ties: if the content box is ever narrower than the bubble, overflowing the
     right (into empty page) is better than overflowing the left (under the rail, unreadable). */
  const targetCenterX = pos.rect.left + pos.rect.width / 2;
  const BUBBLE_MAX_W = Math.min(384, window.innerWidth - 32);
  const EDGE_PAD = 16;
  // Measured box, falling back to the cap on the first frame before it has been measured.
  const halfBubbleW = (bubbleW || BUBBLE_MAX_W) / 2;
  const maxLeft = window.innerWidth - EDGE_PAD - halfBubbleW;
  const minLeft = Math.min(pos.rail + EDGE_PAD + halfBubbleW, maxLeft);
  const clampedLeft = Math.max(minLeft, Math.min(targetCenterX, maxLeft));

  /* WHICH SIDE THE BUBBLE ACTUALLY GOES ON. The step declares a preference; the viewport
     gets a veto. Flip only when the preferred side cannot hold the bubble AND the other one
     can, so on a desktop — where both fit — every step keeps the side it was designed for
     and this is inert. A phone is the case it exists for: the upload and submit controls sit
     near the top of the composer, there is no room above them for a bubble that wraps to
     four or five lines, and there is plenty below. */
  const preferBelow = pos.pointer === 'below';
  const needed = TOTAL_TAIL + bubbleH + EDGE_PAD;
  // Space above the target that the bubble may actually use — the header is not part of it.
  const roomAbove = pos.rect.top - pos.topBar;
  const roomBelow = window.innerHeight - pos.rect.bottom;
  const isBelow = preferBelow
    ? !(roomBelow < needed && roomAbove >= needed)
    : (roomAbove < needed && roomBelow >= needed);

  // Bubble position
  const bubbleStyle = {
    position: 'fixed',
    zIndex: 101,
    left: clampedLeft,
    /* `max-content`, AND THIS IS THE WHOLE PHONE BUG — the width depended on the position,
       and the position is computed from the width.

       A fixed box with `left` set and `width: auto` is SHRINK-TO-FIT against `innerWidth -
       left`, and `left` here is the bubble's CENTRE (it is pulled back by translateX(-50%)).
       So on a 393px iPhone: the first render has no measurement yet, falls back to the 361px
       cap, and centres the bubble at 196 — at which point the box paints 393-196 = 197px wide
       (measured), a little over half the cap, and at half the width it wraps to roughly twice
       the height. THAT is the box the layout effect measures. The measurement then moves the
       centre to 114 and the box repaints wider — but the measuring effect is keyed on the step
       and the target rect, not on its own output, so it never runs again. The placement is
       left holding the width AND THE HEIGHT of a box that is no longer on screen.

       The height is the one that does the damage. The second step is the first to point UP,
       and against that inflated height it cannot fit above the upload button: `top` comes out
       at a few pixels, clamps, and the bubble is drawn against the top of the screen — behind
       the 64px header — with a gap where its tail should be.

       max-content takes `left` out of the sizing entirely: the bubble asks for its text on one
       line and the cap below cuts it down, so the first measurement is also the last. Measured
       on a 393x852 frame, step 2 is now 361x134 and sits above its target at y=84. A desktop
       is untouched, where the cap was always the binding constraint. */
    width: 'max-content',
    maxWidth: BUBBLE_MAX_W,
  };

  /* Anchored by `top` on BOTH sides now. The above-target case used to anchor by `bottom`,
     which cannot be clamped without knowing the height — and it was the unclamped one that
     ran off the top of the screen. With the height measured, one expression covers both and
     Math.max does the guarding. */
  bubbleStyle.top = isBelow
    ? Math.min(pos.rect.bottom + TOTAL_TAIL, window.innerHeight - EDGE_PAD - bubbleH)
    : pos.rect.top - TOTAL_TAIL - bubbleH;
  bubbleStyle.top = Math.max(pos.topBar + EDGE_PAD, bubbleStyle.top);

  // Tail circles positioned between target and bubble
  const tailStyle = {
    position: 'fixed',
    zIndex: 101,
    left: targetCenterX,
    pointerEvents: 'none',
  };

  // Circle 1 (small, closest to target)
  const c1Style = { ...tailStyle };
  // Circle 2 (medium, closer to bubble)
  const c2Style = { ...tailStyle };

  if (isBelow) {
    c1Style.top = pos.rect.bottom + TAIL_GAP;
    c2Style.top = pos.rect.bottom + TAIL_GAP + CIRCLE_1 + 8;
  } else {
    c1Style.bottom = window.innerHeight - pos.rect.top + TAIL_GAP;
    c2Style.bottom = window.innerHeight - pos.rect.top + TAIL_GAP + CIRCLE_1 + 8;
  }

  // Arrow position (between target and tail)
  const arrowStyle = {
    position: 'fixed',
    zIndex: 101,
    left: targetCenterX,
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
  };

  if (isBelow) {
    arrowStyle.top = pos.rect.bottom + 2;
  } else {
    arrowStyle.bottom = window.innerHeight - pos.rect.top + 2;
  }

  // Spotlight style over target element
  const spotlightStyle = {
    position: 'fixed',
    zIndex: 99,
    top: pos.rect.top - 8,
    left: pos.rect.left - 8,
    width: pos.rect.width + 16,
    height: pos.rect.height + 16,
    borderRadius: 16,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
    pointerEvents: 'none',
  };

  return (
    <div className="fixed inset-0 z-[100]" onClick={dismiss}>
      {/* Spotlight cutout */}
      <div style={spotlightStyle} />

      {/* Arrow bouncing toward target */}
      <div style={arrowStyle} className="animate-bounce">
        {isBelow ? (
          <ArrowUp className="w-5 h-5 text-white drop-shadow-lg" />
        ) : (
          <ArrowDown className="w-5 h-5 text-white drop-shadow-lg" />
        )}
      </div>

      {/* Tail circle 1 (small, near target) */}
      <div
        key={`c1-${step}`}
        className="rounded-full bg-white shadow-md"
        style={{
          ...c1Style,
          width: CIRCLE_1,
          height: CIRCLE_1,
          transform: 'translateX(-50%)',
          animation: 'tailFadeIn 0.2s ease-out forwards',
        }}
      />

      {/* Tail circle 2 (medium, near bubble) */}
      <div
        key={`c2-${step}`}
        className="rounded-full bg-white shadow-md"
        style={{
          ...c2Style,
          width: CIRCLE_2,
          height: CIRCLE_2,
          transform: 'translateX(-50%)',
          animation: 'tailFadeIn 0.2s 0.1s ease-out forwards',
          opacity: 0,
        }}
      />

      {/* Bubble */}
      <div
        key={step}
        ref={bubbleRef}
        style={{
          ...bubbleStyle,
          opacity: transitioning ? 0 : 1,
          transition: transitioning ? 'opacity 0.15s ease-out' : 'none',
          animation: !transitioning ? 'bubblePopIn 0.4s ease-out forwards, floatBubble 3s 0.4s ease-in-out infinite' : 'none',
          transform: 'translateX(-50%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-[30px] shadow-2xl px-6 py-5 max-w-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 mt-0.5">
              <Icon className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-gray-800 text-sm leading-relaxed">
              {t(steps[step].textKey)}
            </p>
          </div>

          <div className="flex items-center justify-between">
            {/* Progress dots. A one-step tour has no progress to report — a single dot reads
                as a bullet nobody placed — so the row collapses and the button takes the
                width. Same reason Skip disappears below: with one step it is the other
                button spelled differently. */}
            <div className="flex gap-1.5">
              {steps.length > 1 && steps.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === step ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {steps.length > 1 && (
                <button
                  onClick={dismiss}
                  className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors px-2 py-2"
                >
                  {t('onboarding_skip')}
                </button>
              )}
              <button
                onClick={next}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
              >
                {step < steps.length - 1 ? t('onboarding_next') : t('onboarding_done')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* The composer tour on Home: what the box is, what the paperclip does, what the arrow does. */
const OnboardingBubbles = () => (
  <BubbleTour steps={COMPOSER_STEPS} storageKey={STORAGE_KEY} />
);

/* The one on the Workspace, pointing at the tile of the video that is rendering right now.
   `active` is the caller's business: this must only mount while a generating tile is actually
   on screen, because the tile IS what the bubble is about. See WorkspacePage. */
export const GeneratingBubble = ({ active }) => (
  <BubbleTour steps={GENERATING_STEPS} storageKey={GENERATING_KEY} active={active} seenOnShow />
);

export default OnboardingBubbles;
